/*
The document's own <style> block, read as measurements.

    readStyleSheet(html) -> [rule]          // base rules, in source order
    resolveRules(rules, tag, classes) -> { property: value }
    declarations(styleAttribute)  -> { property: value }

Pure: no React, no DOM, no clock, no network. CommonJS, so it runs under plain
`node src/utils/emailStyleSheet.test.js`.

WHY THIS EXISTS
---------------
The oldest hand-built templates in this corpus — `ps-newsletter-confirmation-*`,
`ps-pro-newsletter-confirmation-*` — write

    <img class="logo" src="…/twitter.png">

with no width attribute and no inline style, and then declare the width once, in
the document's own stylesheet:

    .logo { width: 60px; padding-right: 0.875rem; }

The converter refused those images for having "no measurable width — only a
percentage or a CSS class, and a guessed width would be visibly wrong". The
second half of that sentence is still true and the refusal it protects is still
there; the first half was wrong. The width IS measurable. It is written down, in
the same document, in a rule that applies to that element.

ONLY THE BASE RULES. A declaration inside an `@media` block is what the email
becomes at a breakpoint, not what it is:

    .logo { width: 60px }                        <- base:  the desktop rendering
    @media (max-width:600px) { .logo { 40px } }  <- and 40 on a narrow phone
    @media (max-width:400px) { .logo { 35px } }  <- and 35 on a narrower one

A block document is responsive by being a single column, not by restyling at a
breakpoint, so there is exactly one width it can carry and the base rule is the
one the email actually renders at on a desktop. Every `@media` — and every other
at-rule with a block, `@font-face` included — is skipped whole. `changesIn`
already tells the reviewer the media queries are dropped.

WHAT IS DELIBERATELY NOT MATCHED
--------------------------------
A selector this cannot be certain about is IGNORED rather than approximated.
Three shapes are understood — `h2`, `.logo`, `td.pad` — and nothing else: no
descendant combinators, no attribute selectors, no pseudo-classes, no `*`. The
corpus's `.ii, .ii a[href] { color:#fff !important }` therefore contributes its
`.ii` half and drops the other, which is the conservative direction: a rule that
is not applied leaves the element measured exactly as it was before this module
existed, and the converter's own "no measurable width" refusal still catches
whatever is left unmeasured.

Cascade, for the selectors that ARE understood: tag (0,0,1) then class (0,1,0)
then tag.class (0,1,1), each group in source order, and an inline `style`
attribute — which callers merge on top — beats all of them. That is the real
cascade for these three shapes, not an approximation of it.

`!important` is stripped from values rather than ranked. Within one specificity
group it changes nothing here, and carrying it into a block prop would write
`color:white!important` into the serialised email, which is markup this
converter did not read from anywhere.
*/

/**
 * A style attribute — or a rule body — as a { property: value } map, lower-cased
 * keys, `!important` stripped.
 *
 * Split on `;` rather than parsed: no declaration in this corpus carries a
 * semicolon inside its value, and the one construct that could — `url(...)` in a
 * background-image — is refused outright by the converter before it gets here,
 * because no block type can carry a background image anyway.
 */
function declarations(style) {
  const map = {};
  String(style || '').split(';').forEach((part) => {
    const at = part.indexOf(':');
    if (at === -1) return;
    const key = part.slice(0, at).trim().toLowerCase();
    if (!key || key[0] === '-' || key.indexOf('/*') !== -1) return;
    map[key] = part.slice(at + 1).replace(/!\s*important\s*$/i, '').trim();
  });
  return map;
}

// The three selector shapes this understands, and what each is worth in the
// cascade. Anything else returns null and is dropped.
function parseSelector(text) {
  const selector = String(text).trim();
  let match = /^([a-zA-Z][a-zA-Z0-9-]*)$/.exec(selector);
  if (match) return { tag: match[1].toLowerCase(), className: '', specificity: 1 };
  match = /^\.([a-zA-Z_-][a-zA-Z0-9_-]*)$/.exec(selector);
  if (match) return { tag: '', className: match[1], specificity: 10 };
  match = /^([a-zA-Z][a-zA-Z0-9-]*)\.([a-zA-Z_-][a-zA-Z0-9_-]*)$/.exec(selector);
  if (match) return { tag: match[1].toLowerCase(), className: match[2], specificity: 11 };
  return null;
}

// Every <style> element's contents, comments stripped. Not a scan for tags: the
// pair is matched as a unit, exactly as `textOutside` in the converter cuts the
// same elements out before measuring text.
function styleText(html) {
  const blocks = String(html || '').match(/<style\b[^>]*>[\s\S]*?<\/style>/gi) || [];
  return blocks
    .map(block => block.replace(/^<style\b[^>]*>/i, '').replace(/<\/style>$/i, ''))
    .join('\n')
    .replace(/\/\*[\s\S]*?\*\//g, ' ');
}

/**
 * The document's base style rules, in source order.
 *
 * Each is { tag, className, specificity, order, decl }. A rule whose selector
 * list holds several understood selectors becomes several rules sharing one
 * declaration map, which is what a comma list means.
 *
 * The reader is a brace counter over the stylesheet text, not an HTML scan:
 * a prelude ends at `{` (a rule) or `;` (a statement at-rule like `@import`),
 * and an at-rule with a block — `@media`, `@supports`, `@font-face` — is skipped
 * to its matching `}` so nothing inside one is ever taken for a base rule.
 */
function readStyleSheet(html) {
  const css = styleText(html);
  const blocks = [];
  let cursor = 0;

  while (cursor < css.length) {
    const openAt = css.indexOf('{', cursor);
    const semiAt = css.indexOf(';', cursor);
    if (openAt === -1) break;
    if (semiAt !== -1 && semiAt < openAt) {
      cursor = semiAt + 1;
    } else {
      const prelude = css.slice(cursor, openAt).trim();

      // Walk to the brace that closes this one, so a nested block inside an
      // at-rule cannot end it early.
      let depth = 1;
      let scan = openAt + 1;
      while (scan < css.length && depth > 0) {
        if (css[scan] === '{') depth += 1;
        else if (css[scan] === '}') depth -= 1;
        scan += 1;
      }
      const closed = depth === 0;
      if (prelude[0] !== '@') {
        blocks.push({ prelude, body: css.slice(openAt + 1, closed ? scan - 1 : css.length) });
      }
      cursor = closed ? scan : css.length;
    }
  }

  const rules = [];
  blocks.forEach(({ prelude, body }) => {
    const decl = declarations(body);
    if (Object.keys(decl).length === 0) return;
    prelude.split(',').forEach((one) => {
      const parsed = parseSelector(one);
      if (!parsed) return;
      rules.push({ ...parsed, order: rules.length + 1, decl });
    });
  });

  return rules;
}

/**
 * The declarations that apply to one element, before its own `style` attribute.
 *
 * `classes` is the element's class attribute split on whitespace. Merged in
 * cascade order, so a later or more specific rule wins.
 */
function resolveRules(rules, tag, classes) {
  if (!rules || rules.length === 0) return {};
  const name = String(tag || '').toLowerCase();
  const list = classes || [];
  const matching = rules.filter((rule) => {
    if (rule.tag && rule.tag !== name) return false;
    if (rule.className && list.indexOf(rule.className) === -1) return false;
    return true;
  });
  matching.sort((a, b) => (a.specificity - b.specificity) || (a.order - b.order));
  return matching.reduce((merged, rule) => ({ ...merged, ...rule.decl }), {});
}

module.exports = {
  declarations,
  readStyleSheet,
  resolveRules,
  parseSelector,
};
